<template>
  <div ref="profile" class="account-profile">
    <h2>Личные данные</h2>
    <transition name="fade">
      <ui-loader
        v-if="isLoading"
        class="loader"
        size="60"
      />
    </transition>

    <div class="account-profile-wrapper">
      <ui-input
        v-model="form.lastName"
        label="Фамилия"
        placeholder="Укажите вашу фамилию"
        validate="surname"
        clearable
      />

      <ui-input
        v-model="form.firstName"
        label="Имя*"
        placeholder="Укажите ваше имя"
        validate="name"
        clearable
      />

      <ui-input
        v-model="form.secondName"
        label="Отчество"
        placeholder="Укажите ваше отчество"
        validate="surname"
        clearable
      />

      <div>
        <ui-select
          v-model="form.gender"
          label="Пол*"
          :options="[{name: 'Мужчина', value: 'male'}, {name: 'Женщина', value: 'female'}]"
          placeholder="Не выбран"
          empty-value="Не выбран"
        />
      </div>

      <div>
        <ui-datepicker
          v-model="form.birthday"
          label="Дата рождения*"
          required
        />
      </div>

      <ui-city-suggest
        label="Город*"
        :current="{ id: form.cityId, name: cityName }"
        @change="city => {form.cityId = city.id; cityName = city.name}"
      />
    </div>

    <div class="account-profile-subtitle">
      Семья
    </div>

    <div class="account-profile-wrapper">
      <ui-checkbox
        v-model="spouse"
        label="У меня есть супруг(а)"
      />
      <transition name="fade">
        <ui-datepicker
          v-if="spouse"
          v-model="form.spouse.birthday"
          label="Дата рождения супруга(и)*"
          required
        />
      </transition>
    </div>

    <!-- Дети -->
    <ui-checkbox
      v-model="children"
      label="У меня есть дети"
      class="check-childrens"
    />
    <div class="account-profile-child"
      v-for="(child, i) in form.children"
      :key="`${child.id}-${i}`"
    >
      <div class="account-profile-wrapper">
        <div>
          <ui-select
            v-model="child.gender"
            :options="[{name: 'Мальчик', value: 'male'}, {name: 'Девочка', value: 'female'}]"
            placeholder="Не выбран"
            empty-value="Не выбран"
            label="Пол*"
          />
        </div>
        <ui-datepicker
          v-model="child.birthday"
          class="input birthday"
          validate="child_date"
          label="Дата рождения*"
          required
        />
      </div>
      <div
        class="remove-icon"
        @click="removeChild(i)"
      >
        <f-sprite-icon :icon-link="$options.icons.IconClose.id" />
      </div>
    </div>

    <div
      class="add-button"
      @click="addChild"
      v-if="children && form.children.length < 10"
    >
      <div class="icon">
        <f-sprite-icon :icon-link="$options.icons.IconPlus.id" />
      </div>
      Добавить ребенка
    </div>

    <div class="account-profile-contacts">
      <h2>Контактные данные</h2>
      <profile-contacts class="account-profile-wrapper" />
    </div>

    <div :class="['button', {scrollable: !buttonScrollEnded}]">
      <ui-button
        wide
        large
        text="Сохранить"
        :disabled="!valid || isLoading"
        @click="updateProfile"
      />
    </div>

    <div class="copyright">
      Нажимая на кнопку «Сохранить» вы принимаете условия <a :target="adaptive.isDesktop ? '_blank' : '_self'" href="/agreement">пользовательского соглашения</a>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import ProfileContacts from '@/components/account/profile-contacts.vue';

/* Icons */
import IconPlus from '../../assets/icons/plus.svg';
import IconClose from '../../assets/icons/close.svg';

export default {
  name: 'account-profile',

  components: {
    ProfileContacts
  },

  inject: ['adaptive'],

  data: () => ({
    form: {},
    isLoading: false,
    buttonScrollEnded: false, // скролл кнопки на мобилке
    phone: '',
    cityName: ''
  }),

  icons: {
    IconPlus,
    IconClose,
  },

  computed: {
    ...mapGetters({
      ACCOUNT: 'user/account',
    }),

    valid() {
      return !(
        !this.form.birthday
        || !this.form.gender
        || !this.form.firstName
        || !this.form.cityId
        || (this.form.spouse && !this.form.spouse?.birthday)
        || (this.form.children.length && this.form.children.some(c => !c.birthday))
      )
    },

    spouse: { // есть супруг(а)
      get() {
        return !!this.form.spouse;
      },

      set(exists) {
        if (exists) {
          this.$set(this.form, 'spouse', {
            gender: this.form.gender === 'male' ? 'female' : 'male',
            birthday: ''
          });
        } else {
          this.$delete(this.form, 'spouse');
        }
      }
    },

    children: {
      get() {
        return !!this.form?.children?.length;
      },

      set(val) {
        this.form.children = val ? [{
          gender: '',
          birthday: ''
        }] : [];
      }
    },
  },

  created() {
    this.prefillForm();
  },

  mounted() {
    this.$nextTick(() => {
      this.startObserver();
    })
  },

  beforeDestroy() {
    this.stopObserver();
  },

  methods: {
    ...mapActions({
      UPDATE_PROFILE: 'user/updateProfile',
    }),
    prefillForm() {
      this.form = {
        firstName: this.ACCOUNT.firstName || '',
        secondName: this.ACCOUNT.secondName || '',
        lastName: this.ACCOUNT.lastName || '',
        birthday: this.ACCOUNT.birthday || '',
        gender: this.ACCOUNT.gender || '',
        cityId: this.ACCOUNT.city?.id || this.$store.getters['user/cityId'],
        children: this.ACCOUNT.children || [],
      };
      if (this.ACCOUNT.spouse && Object.keys(this.ACCOUNT.spouse).length) {
        this.form.spouse = this.ACCOUNT.spouse;
      }
    },
    updateProfile() {
      this.isLoading = true;
      this.UPDATE_PROFILE(this.form)
          .then(() => {
            this.isLoading = false;
            this.prefillForm();
          })
          .catch(({ error = {} } = {}) => {
            this.isLoading = false;
            this.$store.dispatch('notifications/create', {
              title: 'Ошибка',
              message: error.detail || 'Не удалось сохранить данные, попробуйте позже',
            });
          });
    },

    // Методы блока "Дети"
    addChild() {
      this.form.children.push({ gender: '', birthday: '' });
    },

    removeChild(index) {
      this.form.children = this.form.children.filter((_, i) => i !== index);
    },

    // Observer Methods (для плавающей кнопки на мобилке)
    startObserver() {
      if (!this.adaptive.isMobile) return;

      this.target = document.querySelector('.copyright');
      if (!this.target) return;
      this.observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          entry.isIntersecting ? this.buttonScrollEnded = true : this.buttonScrollEnded = false;
        })
      });

      this.observer.observe(this.target);
    },

    stopObserver() {
      if (this.observer && this.target) {
        this.observer.unobserve(this.target);
        this.observer = null;
      }
    },
  }
};
</script>

<style lang="scss">
.account-profile {
  position: relative;
  counter-reset: child;
  margin-top: 32px;

  & > .loader {
    position: fixed;
    top: 50%;
    left: 50%;
    z-index: 10;
    transform: translate(-50%, -50%);
    color: $blue;
  }

  &-wrapper {
    display: flex;
    flex-direction: column;
    width: 100%;
    > * {
      margin-top: 0;
      min-height: 84px; // высота инпута с ошибкой итп
    }
  }

  &-subtitle {
    font-size: 14px;
    line-height: 20px;
    margin: 8px 0 0 0;
    font-weight: 600;
  }

  .ui-checkbox {
    margin: 10px 0;
    min-height: 24px;
    align-items: center;
  }

  &-child {
    display: flex;

    &::before {
      counter-increment: child;
      content: counter(child) '.';
      margin: 35px 8px;
      line-height: 20px;
      color: $gray;
    }
    .remove-icon {
      margin: 35px 0 0 12px;
      color: $gray_l;
      height: 20px;
      flex: 0 0 20px;
      cursor: pointer;
    }
  }

  .add-button {
    display: inline-block;
    color: $blue;
    font-weight: 600;
    font-size: 14px;
    line-height: 24px;
    margin-top: 4px;
    cursor: pointer;

    .icon {
      float: left;
      width: 24px;
      height: 24px;
      padding: 7px;
      margin-right: 8px;
      background-color: rgba(26, 120, 187, .08);
      border-radius: 50%;
    }
  }

  &-contacts {
    margin-top: 24px;
    > h2 {
      margin-top: 0;
    }
  }

  .button {
    position: sticky;
    bottom: 0;
    margin: 16px -16px 0;
    padding: 16px;

    &.scrollable {
      background-color: #fff;
      box-shadow: inset 0 1px 0 $gray_lu;
    }
  }

  .copyright {
    font-size: 12px;
    line-height: 16px;
    color: $gray_l;

    a {
      color: $black;
    }
  }
}

@include breakpoint("tablet") {
  .account-profile {
    &-wrapper {
      flex-direction: row;
      flex-wrap: wrap;
      > * {
        flex: 0 0 33.3333%;
        margin-top: 12px;
        &:not(:nth-child(3n+3)) {
          padding-right: 24px;
        }
      }
    }

    &-subtitle {
      font-size: 16px;
      line-height: 24px;
      margin: 8px 0 0 0;
    }

    .ui-checkbox {
      min-height: 96px;
      margin: 0;
    }
    .check-childrens {
      margin: -32px 0 -24px;
    }

    &-child {
      display: block;
      position: relative;
      &::before {
        position: absolute;
        margin: 0;
        top: 46px;
      }
      > .account-profile-wrapper {
        > *:first-child {
          padding-left: 20px;
        }
      }
      .remove-icon {
        position: absolute;
        top: 44px;
        left: 66.6667%;
        color: $gray_l;
        width: 24px;
        height: 24px;
        margin: 0;
      }
    }

    .add-button {
      margin: 12px 0 36px;
    }

    .button {
      position: relative;
      display: inline-block;
      width: 113px;
      margin: 40px 24px 0 0;
      padding: 0;
    }

    .copyright {
      display: inline-block;
    }
  }
}

@include breakpoint("desktop") {
  .account-profile {
    margin-top: 0;

    &-contacts {
      margin-top: 36px;
    }

    .button {
      min-width: 123px;
      margin-top: 56px;
      margin-right: 40px;
    }
  }
}
</style>
